import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from './Header/Header';
import Sidebar from './Sidebar/Sidebar';
import Footer from './Footer/Footer';
import BottomTab from './BottomTab/BottomTab';
import { useScreenSize } from '@/hooks';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { useGames } from '@/Context/GameContext';
import styles from './Layout.module.css';

/**
 * Layout Component
 * Main layout wrapper with Header, Sidebar, and Content area
 * Follows JACKOPOT design system
 */
const Layout = () => {
  const { isMobile } = useScreenSize();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentGame, closeGame, showIframe } = useGames();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Apply smooth scroll animation on route change
  useSmoothScroll();

  // Check if current route is a game route
  // Exclude affiliate routes, sports routes, menu, and other non-game routes
  const isGameRoute = location.pathname.match(/^\/[^/]+\/[^/]+$/) && 
                      !location.pathname.startsWith('/sports/') && 
                      !location.pathname.startsWith('/match/') &&
                      !location.pathname.startsWith('/menu') &&
                      !location.pathname.startsWith('/affiliate') &&
                      !location.pathname.startsWith('/casino') &&
                      !location.pathname.startsWith('/settings') &&
                      !location.pathname.startsWith('/promotions') &&
                      !location.pathname.startsWith('/vip-club') &&
                      !location.pathname.startsWith('/bonus-page') &&
                      !location.pathname.startsWith('/support') &&
                      !location.pathname.startsWith('/gift-cards') &&
                      !location.pathname.startsWith('/provably-fair');

  // Check if current route is sports-related (no sidebar)
  // Include both /sports and /sports/:sport
  const isSportsRoute = location.pathname.startsWith('/sports') || 
                        location.pathname.startsWith('/match/') ||
                        location.pathname.startsWith('/menu');

  // Sidebar is always expanded by default - only collapses when manually toggled

  const handleMenuToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Handle sidebar item click - Don't auto-collapse on navigation
  // Sidebar should remain open when navigating to routes
  const handleSidebarItemClick = () => {
    // Sidebar stays open - user can manually toggle if needed
    // This provides better UX for navigation, especially for Casino routes
  };

  const handleGameBack = () => {
    closeGame(navigate);
  };

  // Get provider display name
  const getProviderName = () => {
    if (currentGame?.provider) {
      return currentGame.provider.charAt(0).toUpperCase() + currentGame.provider.slice(1);
    }
    const pathParts = location.pathname.split('/');
    if (pathParts.length > 1 && pathParts[1]) {
      return pathParts[1].charAt(0).toUpperCase() + pathParts[1].slice(1);
    }
    return 'Game';
  };

  return (
    <div className={styles.layout}>
      {/* Mobile Game Header - Only show when game is active and on mobile */}
      {isMobile && showIframe && (
        <div className={styles.mobileGameHeader}>
          <button 
            className={styles.gameBackButton}
            onClick={handleGameBack}
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className={styles.gameProviderName}>{getProviderName()}</h2>
        </div>
      )}

      {/* Regular Header - Hide on mobile when game is active */}
      {!(isMobile  && showIframe) && (
        <Header 
          onMenuToggle={handleMenuToggle}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      )}

      <div className={styles.contentWrapper}>
        {/* Sidebar (Desktop only) */}
        {!isMobile && (
          <Sidebar 
            isCollapsed={isSidebarCollapsed}
            onToggle={handleMenuToggle}
            onItemClick={handleSidebarItemClick}
          />
        )}

        <div 
          className={`${styles.mainContentArea} ${isSidebarCollapsed && !isMobile ? styles.mainContentAreaCollapsed : ''}`}
        >
          {/* Main Content */}
          <main className={`${styles.main} ${isGameRoute && showIframe ? styles.gameMain : ''}`}>
            <Outlet />
          </main>

          {/* Footer */}
          {!isSportsRoute && !isGameRoute && <Footer />}
        </div>
      </div>

      {/* Bottom Tab Navigation (Mobile only) - Hide when game is active */}
      {!(isMobile && isGameRoute && showIframe) && <BottomTab />}
    </div>
  );
};

export default Layout;
